import { Link } from "@tanstack/react-router";
import { BarChart3, ClipboardList, Euro, Percent } from "lucide-react";
import { useCommercialDashboard } from "../api";
import { CommercialHeader } from "../components/CommercialHeader";
import { useCommercialStore } from "../store";

const eur = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });
const date = new Intl.DateTimeFormat("es-ES", { dateStyle: "short" });

export function CommercialDashboardPage() {
  const { emisorId } = useCommercialStore();
  const { data, isLoading } = useCommercialDashboard(emisorId);

  const cards = [
    { label: "Valoraciones", value: data?.total ?? 0, icon: ClipboardList },
    { label: "Borradores", value: data?.borradores ?? 0, icon: BarChart3 },
    { label: "Aprobadas", value: data?.aprobadas ?? 0, icon: Percent },
    { label: "Importe aprobado", value: eur.format(data?.importe_aprobado ?? 0), icon: Euro },
  ];

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <CommercialHeader title="Resumen comercial" subtitle="Valoraciones, importe aprobado y actividad reciente por empresa." />

      {!emisorId ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm font-semibold text-amber-800">Selecciona una empresa para consultar el módulo.</div>
      ) : isLoading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm font-semibold text-slate-500">Cargando datos comerciales…</div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {cards.map(({ label, value, icon: Icon }) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-black uppercase tracking-wide text-slate-500">{label}</p>
                  <Icon size={18} className="text-emerald-700" />
                </div>
                <p className="mt-3 text-2xl font-black text-slate-900">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-5 xl:grid-cols-[1.5fr_.5fr]">
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <h2 className="font-black text-slate-900">Valoraciones recientes</h2>
                  <p className="text-sm text-slate-500">Últimos registros de la empresa seleccionada.</p>
                </div>
                <Link to="/commercial/valuations" className="text-sm font-black text-emerald-700 hover:text-emerald-900">Ver todas</Link>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr><th className="px-5 py-3">Nº</th><th className="px-5 py-3">Cliente</th><th className="px-5 py-3">Concepto</th><th className="px-5 py-3">Fecha</th><th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3">Estado</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data?.recientes.length ? data.recientes.map((item) => (
                      <tr key={item.id}>
                        <td className="px-5 py-3 font-bold text-slate-700">#{item.id}</td>
                        <td className="px-5 py-3 font-semibold text-slate-800">{item.razon_social}</td>
                        <td className="px-5 py-3 text-slate-600">{item.concepto}</td>
                        <td className="px-5 py-3 text-slate-500">{item.created_at ? date.format(new Date(item.created_at)) : "—"}</td>
                        <td className="px-5 py-3 text-right font-bold text-slate-900">{eur.format(item.total)}</td>
                        <td className="px-5 py-3"><span className={item.estado === "aprobado" ? "rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-700" : "rounded-full bg-amber-50 px-2.5 py-1 text-xs font-black text-amber-700"}>{item.estado === "aprobado" ? "Aprobado" : "Borrador"}</span></td>
                      </tr>
                    )) : <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">Todavía no hay valoraciones.</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-black uppercase tracking-wide text-slate-500">Beneficio aprobado</p>
              <p className="mt-2 text-3xl font-black text-emerald-700">{eur.format(data?.beneficio_aprobado ?? 0)}</p>
              <p className="mt-3 text-sm leading-6 text-slate-500">Se calcula exclusivamente con valoraciones aprobadas del nuevo módulo Comercial.</p>
              <Link to="/commercial/valuations" className="mt-5 inline-flex rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800">Gestionar valoraciones</Link>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
